/**
 * homeSummary.ts
 *
 * Pure model for the signed-in home page: one summary card per product the
 * tenant is entitled to. Each card shows three counts (needs you, running,
 * this week) and the three most recently changed rows. No React, no I/O.
 *
 * Counts reuse the Feature home predicates (`rowMatchesStat`), so a card here
 * agrees with the stat card of the same name on the Feature home.
 */

import { JobType } from '../../graphql/types';
import { MODE_LABELS } from '../../workspace/modes';
import { getRowMode } from '../../workspace/rowMode';
import type { PillTone } from '../ui/Pill';
import type { StatusDotTone } from '../ui/StatusDot';
import type { Product, ProductId } from '../featureLayout/products';
import {
  PLACEHOLDER,
  getUpdatedAt,
  gradeTone,
  rowMatchesStat,
  type HomeRow,
  type StatId,
} from './featureHomeModel';

/** How many recent rows a card lists. */
export const RECENT_LIMIT = 3;

export interface ProductCopy {
  description: string;
  singular: string;
  plural: string;
  /** Caption under the "Running" count. */
  runningCaption: string;
  /** Caption under the "This week" count. */
  weekCaption: string;
  /** Predicate behind the "Running" count. */
  runningStat: StatId;
  viewAll: string;
  startNew: string;
}

export const PRODUCT_COPY: Readonly<Record<ProductId, ProductCopy>> = {
  DD: {
    description: 'Review and verify loan documents for due diligence',
    singular: 'job',
    plural: 'jobs',
    runningCaption: 'extracting',
    weekCaption: 'completed',
    runningStat: 'extracting',
    viewAll: 'View all jobs',
    startNew: 'Start a job',
  },
  BA: {
    description: 'Analyze borrower documents for underwriting',
    singular: 'loan',
    plural: 'loans',
    runningCaption: 'extracting',
    weekCaption: 'completed',
    runningStat: 'extracting',
    viewAll: 'View all loans',
    startNew: 'Start a loan',
  },
  TR: {
    description: 'Search recorded documents and grade the title',
    singular: 'search',
    plural: 'searches',
    runningCaption: 'processing',
    weekCaption: 'graded',
    runningStat: 'inProgress',
    viewAll: 'View all searches',
    startNew: 'Start a title search',
  },
};

export interface HomeStat {
  id: 'needsYou' | 'running' | 'week';
  label: string;
  count: number;
  /** Unit or state under the count: "jobs", "extracting", "completed". */
  caption: string;
}

export interface RecentItem {
  jobId: string;
  href: string;
  title: string;
  sub: string;
  dot: StatusDotTone;
  pillLabel: string;
  pillTone: PillTone;
}

export interface ProductSummary {
  id: ProductId;
  name: string;
  description: string;
  viewAllLabel: string;
  startNewLabel: string;
  unitPlural: string;
  stats: HomeStat[];
  recent: RecentItem[];
}

function present(value: string | undefined): value is string {
  return !!value && value !== PLACEHOLDER;
}

function titleOf(row: HomeRow): string {
  const { cells, job } = row;
  const value =
    job.jobType === JobType.BA ? cells.borrower : job.jobType === JobType.TR ? cells.property : cells.name;
  return present(value) ? value : (cells.name ?? PLACEHOLDER);
}

function subOf(row: HomeRow): string {
  const { cells, job } = row;
  let identifier: string | undefined;
  if (job.jobType === JobType.DD) {
    identifier = (job.loanCount ?? 0) > 0 ? cells.loans : undefined;
  } else if (job.jobType === JobType.BA) {
    identifier = cells.loanNumber;
  }
  return [identifier, row.needsYou ?? row.stageLabel].filter(present).join(' · ');
}

function dotOf(row: HomeRow): StatusDotTone {
  if (row.blocked) return 'danger';
  if (row.needsYou) return 'warning';
  if (row.stageKey === 'processing' || row.stageKey === 'analysis') return 'running';
  if (row.stageKey === 'complete' || row.stageKey === 'ready') return 'success';
  return 'idle';
}

function pillOf(row: HomeRow): { label: string; tone: PillTone } {
  if (row.blocked) return { label: 'Failed', tone: 'danger' };
  if (row.stageKey === 'complete') {
    if (row.grade) {
      return {
        label: `Grade ${row.grade}`,
        tone: gradeTone(row.grade) === 'success' ? 'success' : 'warning',
      };
    }
    return { label: 'Completed', tone: 'success' };
  }
  if (row.stageKey === 'ready') return { label: 'Ready', tone: 'success' };
  if (row.stageKey === 'processing' || (row.job.jobType === JobType.TR && row.stageKey === 'analysis')) {
    return { label: 'Running', tone: 'neutral' };
  }
  // Otherwise name the Mode the row opens in.
  return { label: MODE_LABELS[getRowMode(row.job)], tone: row.needsYou ? 'warning' : 'neutral' };
}

function updatedAtMs(row: HomeRow): number {
  const ms = new Date(getUpdatedAt(row.job)).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/** Build one product's card from its rows. */
export function buildProductSummary(
  product: Product,
  rows: readonly HomeRow[],
  now: Date = new Date()
): ProductSummary {
  const copy = PRODUCT_COPY[product.id];
  const count = (stat: StatId) => rows.filter((row) => rowMatchesStat(row, stat, now)).length;
  const needsYou = count('needsYou');

  const stats: HomeStat[] = [
    {
      id: 'needsYou',
      label: 'Needs you',
      count: needsYou,
      caption: needsYou === 1 ? copy.singular : copy.plural,
    },
    { id: 'running', label: 'Running', count: count(copy.runningStat), caption: copy.runningCaption },
    { id: 'week', label: 'This week', count: count('closedWeek'), caption: copy.weekCaption },
  ];

  const recent: RecentItem[] = [...rows]
    .sort((a, b) => updatedAtMs(b) - updatedAtMs(a))
    .slice(0, RECENT_LIMIT)
    .map((row) => {
      const pill = pillOf(row);
      return {
        jobId: row.job.jobId,
        href: `${product.href}/${row.job.jobId}`,
        title: titleOf(row),
        sub: subOf(row),
        dot: dotOf(row),
        pillLabel: pill.label,
        pillTone: pill.tone,
      };
    });

  return {
    id: product.id,
    name: product.name,
    description: copy.description,
    viewAllLabel: copy.viewAll,
    startNewLabel: copy.startNew,
    unitPlural: copy.plural,
    stats,
    recent,
  };
}

// --- Greeting ----------------------------------------------------------------

export function getGreeting(now: Date = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** "Monday, October 19" (rendered upper-case by the overline style). */
export function formatHomeDate(now: Date = new Date()): string {
  return now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

/** First name from a display name or, failing that, an email address. */
export function getFirstName(name?: string | null, email?: string | null): string | null {
  const fromName = (name ?? '').trim().split(/\s+/)[0];
  if (fromName) return fromName;
  const local = (email ?? '').split('@')[0]?.split(/[._-]/)[0];
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : null;
}
